import {
  IonContent,
  IonHeader,
  IonModal,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import closeIcon from "../../../assets/imgs/close.svg";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { Colors } from "../../Commons/Colors/Colors";
import TableCom from "../../Commons/Tabel/Table";
import searchIcon from "../../../assets/imgs/Search.svg";
import { categoriesFilters, positions } from "../../Rankings/data";
import useFirebaseAnalytics from "../../../hooks/useFirabaseAnality";
import { hapticsImpactLight, rankingHeaders } from "../../../lib/utils";
import TableTabs from "../../Commons/TableTabs/TableTabs";
import { Capacitor } from "@capacitor/core";
import { useMixpanel } from "../../../../src/Context/MixpanelContext/MixpanelProvider";
import { useSelector } from "react-redux";
import { actions } from "../../../../src/Redux/store";
import ChatbotImg from "../../../assets/imgs/ai-chat-logo.svg";
import {
  useRanking2024Season,
  useRanking2025Season,
} from "../../../../src/Controllers/PlayerRankingApi";
import RankingTableTabs from "../../Commons/RankingTableTabs/RankingTableTabs";
import PlayerRankingSkeleton from "./PlayerRankingSkeleton";
import { Paragraph } from "../../Commons/Paragraph";
interface PlayerRankingModalProps {
  isOpen: boolean;
  toggle: () => void;
}

type Player = {
  position: string;
  name: string;
  [key: string]: any;
};

type CategoryItem = {
  id: number;
  name: string;
};

type PositionItem = {
  id: number;
  name: string;
};

const defaultPosition = { id: 1, name: "All" };
const rosRankPosition = { id: 2, name: "QB" };
const defaultCategory = { id: 1, name: "2024 Season" };

export default function PlayerRanking() {
  const params = new URLSearchParams(location.search);
  const PosId = params.get("pos");
  const PerId = params.get("per");
  const { logEventAnalytics } = useFirebaseAnalytics();
  const Mixpanel = useMixpanel();
  const isOpen = useSelector(
    (sate: any) => sate.modal.playerRankingModal?.open,
  );
  const toggle = () => {
    actions.modal.closePlayerRankingModal(null);
  };
  // Initialize states with memoized default values
  const defaultPlayerPosition = categoriesFilters.find(
    (item) => item.id === Number(PerId),
  ) || {
    id: 2,
    name: "2025 Season",
  };

  const defaultPosition = positions.find(
    (item) => item.id === Number(PosId),
  ) || {
    id: 1,
    name: "All",
  };

  const [playerPosition, setplayerPosition] = useState<CategoryItem>(
    defaultPlayerPosition,
  );
  const [category, setCategory] = useState<CategoryItem>(defaultPlayerPosition);
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const [positionSelected, setPosicionSelected] =
    useState<PositionItem>(defaultPosition);
  const [searchTerm, setSearchTerm] = useState<string | null | undefined>();
  const [playersData, setPlayersData] = useState<Player[]>([]);
  const [stockWatchPlayersData, setStockWatchPlayersData] = useState<Player[]>(
    [],
  );
  const [isPlayerProfile, setIsPlayerProfile] = useState(false);
  const [playerId, setplayerId] = useState<string>("");
  const postFix = process.env.REACT_APP_ENV === "staging" ? "_stg" : "_prod";
  const rankingsLastUpdated = `rankings_last_updated${postFix}`;
  // Add a ref for the search input
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Fetch required data using hooks - only when modal is open
  const ranking2024Season = useRanking2024Season({ enabled: isOpen });
  const ranking2025Season = useRanking2025Season({ enabled: isOpen });

  const isLoading =
    ranking2025Season?.isLoading || ranking2024Season?.isLoading;

  // Track modal open event
  useEffect(() => {
    if (isOpen) {
      Mixpanel.trackEvent("rankings_modal_open_by_user", {
        name: "Player Rankings modal open by user",
      });
    }
  }, [isOpen, Mixpanel]);

  // Memoize data fetching function
  const fetchDatabyCategory = useCallback(
    (categoryName: string) => {
      switch (categoryName) {
        case "2024 Season":
          return ranking2024Season?.data;
        case "2025 Season":
          return ranking2025Season?.data;
        default:
          return ranking2024Season?.data;
      }
    },
    [ranking2024Season?.data, ranking2025Season?.data],
  );

  // Memoize player filtering function
  const filterPlayers = useCallback(() => {
    const categoryData = fetchDatabyCategory(category.name);
    if (!categoryData?.data) return [];

    let filteredPlayers = categoryData.data;

    if (isSearch && searchTerm) {
      filteredPlayers = filteredPlayers.filter(
        (player: any) =>
          player.playerName.toLowerCase().includes(searchTerm.toLowerCase()) &&
          (positionSelected.name === "All" ||
            player.playerPosition === positionSelected.name),
      );
    } else if (positionSelected.name !== "All") {
      filteredPlayers = filteredPlayers.filter(
        (player: any) => player.playerPosition === positionSelected.name,
      );
    }
    return filteredPlayers;
  }, [
    category.name,
    isSearch,
    searchTerm,
    positionSelected.name,
    fetchDatabyCategory,
  ]);

  // Update players data when filters change
  useEffect(() => {
    const filteredPlayers = filterPlayers();
    setPlayersData(filteredPlayers);
  }, [filterPlayers]);

  // Update stock watch players when players data changes
  useEffect(() => {
    const stockWatchPlayersIds = [
      "joshalle1",
      "justjeff1",
      "patrmaho1",
      "georkitt1",
    ];
    const stockWatchPlayers = playersData.filter((player) =>
      stockWatchPlayersIds.includes(player.roto_player_id),
    );
    setStockWatchPlayersData(stockWatchPlayers);
  }, [playersData]);

  // Handler functions
  const handleSelect = useCallback((item: CategoryItem) => {
    setplayerPosition(item);
    setCategory(item);
  }, []);

  const handleSelectPosition = useCallback((item: PositionItem) => {
    Mixpanel.trackEvent("rankings_position_filter", {
      name: `Rankings filtered by ${item.name} position`,
    });
    setPosicionSelected(item);
  }, []);

  const handleSearchBar = useCallback(() => {
    hapticsImpactLight();
    logEventAnalytics("rankings_search_initiated", {
      name: "Search Bar Click",
    });
    Mixpanel.trackEvent("rankings_search_initiated", {
      name: "Search Bar Click",
    });
    setIsSearch((prev) => !prev);
  }, [logEventAnalytics]);

  // Add effect to focus input when isSearch becomes true with a delay
  useEffect(() => {
    let focusTimeout: NodeJS.Timeout;

    if (isSearch && searchInputRef.current) {
      // Add a small delay to ensure component is fully rendered
      focusTimeout = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 1000); // 150ms delay should be sufficient for the component to render
    }

    // Clean up timeout if component unmounts or isSearch changes
    return () => {
      if (focusTimeout) {
        clearTimeout(focusTimeout);
      }
    };
  }, [isSearch]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setIsSearch((prev) => !prev);
  }, []);

  const togglePlayerProfile = useCallback(() => {
    setIsPlayerProfile((prev) => !prev);
  }, []);

  const setPlayerFromTable = (id: string) => {
    setplayerId(id);
  };

  const handleFilterSearch = (value: string) => {
    setSearchTerm(value);
  };
  const platform = Capacitor.getPlatform();

  // Reset all states to default values when modal closes
  const handleClose = () => {
    setplayerPosition(defaultPlayerPosition);
    setCategory(defaultPlayerPosition);
    setIsSearch(false);
    setPosicionSelected(defaultPosition);
    setSearchTerm("");
    setIsPlayerProfile(false);
    modal.current?.dismiss();
    toggle();
  };

  const modal = useRef<HTMLIonModalElement>(null);

  const headers = useMemo(() => {
    if (positionSelected?.name === "QB" && category?.id === 2) {
      return [rankingHeaders.QB_2025];
    } else if (positionSelected?.name === "RB" && category?.id === 2) {
      return [rankingHeaders.RB_2025];
    } else if (positionSelected?.name === "WR" && category?.id === 2) {
      return [rankingHeaders.WR_2025];
    } else if (positionSelected?.name === "TE" && category?.id === 2) {
      return [rankingHeaders.TE_2025];
    } else if (positionSelected?.name === "All" && category?.id === 2) {
      return [rankingHeaders.ALL_2025];
    } else if (
      positionSelected?.name === "QB" &&
      category?.name === "2024 Season"
    ) {
      return [rankingHeaders.QB_2024];
    } else if (
      positionSelected?.name === "RB" &&
      category?.name === "2024 Season"
    ) {
      return [rankingHeaders.RB_2024];
    } else if (
      positionSelected?.name === "WR" &&
      category?.name === "2024 Season"
    ) {
      return [rankingHeaders.WR_2024];
    } else if (
      positionSelected?.name === "TE" &&
      category?.name === "2024 Season"
    ) {
      return [rankingHeaders.TE_2024];
    } else if (category?.name === "2024 Season") {
      return [rankingHeaders.season2024];
    } else if (category?.name === "2025 Season") {
      return [rankingHeaders.season2025];
    } else {
      return [rankingHeaders.season2024];
    }
  }, [positionSelected?.name, category?.id, category?.name]);

  return (
    <>
      <IonModal
        onDidDismiss={handleClose}
        isOpen={isOpen}
        backdropDismiss={false}
        className={"full-modal"}
        ref={modal}
      >
        <IonHeader class="ion-no-border modal-ion-header">
          <IonToolbar
            className="mt-[15px]"
            style={{
              "--background": "none",
            }}
          >
            <div className=" flex justify-between items-center sticky top-0  z-50   md:mx-2">
              <div onClick={handleClose} className="cursor-pointer p-2">
                <img
                  src={closeIcon}
                  alt="close"
                  className="w-[15px] h-[15px]"
                />
              </div>

              <IonTitle>
                <div className="flex flex-row items-center justify-center">
                  <img
                    src={ChatbotImg}
                    alt="AI Chat"
                    className="mr-2  w-[30.8px] h-[23.7px]"
                  />
                  <span className=" [@media(min-width:375px)]:text-[22px] text-[20px] font-medium text-white">
                    Rankings
                  </span>
                </div>
              </IonTitle>
            </div>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {isLoading ? (
            <PlayerRankingSkeleton itemCount={15} />
          ) : (
            <div className="flex h-full relative">
              <div className="w-full">
                <div className="mt-5 flex items-center gap-2 p-2 pt-[0px]">
                  <div className="flex-1 overflow-x-auto no-scrollbar ">
                    <RankingTableTabs
                      tabItems={categoriesFilters}
                      tabCustomStyle="h-[34px] px-[11.5px] shrink-0 text-[13.8px] rounded-[31.05px]"
                      onTabSelect={handleSelect}
                      InicialselectedTab={playerPosition}
                      selectedTabColor={"#08262A"}
                    />
                  </div>
                </div>
                <div className="p-2 pt-[0px]">
                  {/* Search row - takes full width */}
                  <div className="mt-3">
                    <div className="flex items-center w-full">
                      <div className="relative flex items-center flex-1">
                        <span className="absolute left-2 text-white/70 z-10">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-[12.49px] w-[12.25px]"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M12.9 14.32a8 8 0 111.42-1.42l4.31 4.3a1 1 0 01-1.42 1.42l-4.3-4.31zM8 14a6 6 0 100-12 6 6 0 000 12z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                        <input
                          ref={searchInputRef}
                          type="text"
                          placeholder="Search player name..."
                          value={searchTerm ? searchTerm : ""}
                          onChange={(e) => handleFilterSearch(e.target.value)}
                          className="text-white h-[40px]  placeholder:text-white/70 text-[12px] pl-7 py-2 rounded-[10px] outline-none focus:outline-none bg-white/10 w-full"
                          onClick={!isSearch ? handleSearchBar : undefined}
                        />
                      </div>
                      {isSearch && (
                        <button
                          onClick={handleClearSearch}
                          className="text-white text-[10px] font-normal h-[28px] pl-[12px] pr-[8px] flex items-center justify-center"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Position filters row - appears below search */}
                  <div className="mt-3 overflow-x-auto no-scrollbar">
                    <RankingTableTabs
                      tabItems={positions}
                      tabCustomStyle="w-[50px] h-[28px] shrink-0 text-[12px] rounded-[8px]"
                      onTabSelect={handleSelectPosition}
                      InicialselectedTab={positionSelected}
                      positionTab={true}
                    />
                  </div>
                </div>

                <div className="flex-1 pb-5 pt-3">
                  <TableCom
                    tableHeader={headers}
                    data={playersData}
                    category={category}
                    positionSelected={positionSelected}
                    onSentEventFirebase={(
                      name: string,
                      params?: { [key: string]: any },
                    ) => {
                      logEventAnalytics(name, params);
                    }}
                    posID={positionSelected.id}
                    perID={category.id}
                    togglePlayerProfile={true}
                    tableSorte={true}
                  />
                </div>
              </div>
            </div>
          )}
        </IonContent>
      </IonModal>
    </>
  );
}
